if (!customElements.get('similar-in-my-size')) {
  customElements.define(
    'similar-in-my-size',
    // eslint-disable-next-line no-undef
    class SimilarInMySize extends window.BAO.CustomElement() {
      setupListeners () {
        super.setupListeners()

        this.initTagalysWidget = this.initTagalysWidget.bind(this)
        this.beforeAPICall = this.beforeAPICall.bind(this)
        this.onSuccessfulAPICall = this.onSuccessfulAPICall.bind(this)
        this.onFailedAPICall = this.onFailedAPICall.bind(this)
        this.onVariantChange = this.onVariantChange.bind(this)

        this.listeners.add(window, 'tagalys:similar-size', this.onVariantChange)

        this.queueInitialFetch()
      }

      queueInitialFetch (attempt = 0) {
        const run = () => {
          if (!window.Tagalys?.UIWidgets?.Recommendations) {
            if (attempt < 20) {
              window.setTimeout(() => this.queueInitialFetch(attempt + 1), 100)
            }
            return
          }

          this.syncSelectedSize()

          if (!this.dataset.variantSize) {
            if (attempt < 10) {
              window.setTimeout(() => this.queueInitialFetch(attempt + 1), 100)
            }
            return
          }

          if (!this.isSelectedVariantUnavailable()) return

          this.skipSizeScope = false
          this.initTagalysWidget()
        }

        if (attempt === 0 && typeof window.onTagalysReady === 'function') {
          window.onTagalysReady(run)
          return
        }

        run()
      }

      fetchForSelectedVariant (variantData) {
        this.syncSelectedSize(variantData)

        if (
          !this.dataset.variantSize ||
          !this.isSelectedVariantUnavailable(variantData)
        ) {
          return
        }

        this.skipSizeScope = false
        this.initTagalysWidget()
      }

      async onVariantChange (e) {
        if (e.detail.variant?.data?.available === true) return

        this.dataset.variantId = e.detail.variant?.data?.id
        this.fetchForSelectedVariant(e.detail.variant?.data)
      }

      getProductVariants () {
        const productId = this.dataset.productId
        const scripts = document.querySelectorAll(
          '[data-variant-radios-el="data"], [data-variant-selects-el="data"]',
        )
        const script = Array.from(scripts).find(
          el => !productId || el.dataset.productId === productId,
        )

        if (!script) return []

        try {
          return JSON.parse(script.textContent)
        } catch (error) {
          return []
        }
      }

      getSelectedProductVariant (variantData) {
        if (variantData?.id) return variantData

        const variants = this.getProductVariants()
        const position = window.BAO.TagalysLibrary.getSizeOptionPosition(
          this.dataset.productId,
        )
        const size =
          this.dataset.variantSize ||
          window.BAO.TagalysLibrary.getSelectedSize(this.dataset.productId)

        if (size) {
          const matchBySize = variants.find(
            variant => variant[`option${position}`] === size,
          )
          if (matchBySize) return matchBySize
        }

        const urlVariantId = new URLSearchParams(window.location.search).get(
          'variant',
        )
        const formVariantId = document.querySelector(
          'form[action*="/cart/add"] [name="id"]',
        )?.value
        const variantId =
          urlVariantId || formVariantId || this.dataset.variantId

        if (!variantId) return null

        return (
          variants.find(variant => String(variant.id) === String(variantId)) ||
          null
        )
      }

      isSelectedVariantUnavailable (variantData) {
        if (this.dataset.variantAvailable === 'false') return true

        const triggerAvailable = this.externalEls?.trigger?.element?.dataset
          .variantAvailable
        if (triggerAvailable === 'false') return true

        return this.getSelectedProductVariant(variantData)?.available === false
      }

      syncSelectedSize (variantData) {
        const productId = this.dataset.productId
        const sizeFromDom = window.BAO.TagalysLibrary.getSelectedSize(productId)

        if (sizeFromDom) this.dataset.variantSize = sizeFromDom

        const position = window.BAO.TagalysLibrary.getSizeOptionPosition(
          productId,
        )
        const selectedVariant = this.getSelectedProductVariant(variantData)
        const sizeFromVariant = selectedVariant?.[`option${position}`]

        this.dataset.variantSize =
          sizeFromDom || sizeFromVariant || this.dataset.variantSize || ''
      }

      getCarouselElement () {
        return (
          this.els?.carousel?.element ||
          this.querySelector('[data-similar-in-my-size-el="carousel"]')
        )
      }

      initTagalysWidget () {
        if (!window.Tagalys?.UIWidgets?.Recommendations) return

        const headerElements = this.externalEls?.header?.elements
        const carousel = this.getCarouselElement()
        const tagalysWidgetId = carousel?.dataset.tagalysWidgetId
        if (!carousel || !tagalysWidgetId) return

        if (!carousel.id) {
          carousel.id = `sis-carousel-${this.dataset.productId || 'product'}`
        }

        const carouselSelector = `#${carousel.id}`

        headerElements?.forEach(el => {
          const defaultTitle = el.dataset.defaultTitle
            ? el.dataset.defaultTitle
            : theme.strings.similarStyles

          el.innerText = this.dataset.variantSize
            ? theme.strings.similarStylesWithSize.replace(
              '[size]',
              this.dataset.variantSize,
            )
            : defaultTitle
        })

        window.Tagalys.UIWidgets.Recommendations.init(carouselSelector, {
          recommendationId: tagalysWidgetId,
          ...(this.dataset.productId?.length
            ? { productIds: [this.dataset.productId] }
            : {}),
          templates: {
            widget: {
              options: {
                includeTagalysCSS: false,
                selector: carouselSelector,
              },
              render: () => {},
            },
            product: {
              render: () => {},
            },
          },
          callbacks: {
            beforeAPICall: this.beforeAPICall,
            onSuccessfulAPICall: this.onSuccessfulAPICall,
            onFailedAPICall: this.onFailedAPICall,
          },
        })
      }

      beforeAPICall (params) {
        // `_variant_size` is indexed for English "Size" catalogues (GB, US).
        // Translated option names (Taille, Größe) leave the facet empty, so
        // skip it on retry and filter to the selected size in the callback.
        if (this.skipSizeScope || !this.dataset.variantSize) {
          return params
        }

        return {
          ...params,
          scope: {
            _variant_size: [this.dataset.variantSize],
          },
        }
      }

      onSuccessfulAPICall (response) {
        if (!this.isValidTagalysResponse(response)) {
          this.setComponentVisibility(false)
          return response
        }

        const products = this.getProductsInSelectedSize(response.products)

        if (products.length > 0) {
          this.renderCustomProducts(products)
          this.setComponentVisibility(true)
          return { ...response, products }
        }

        if (!this.skipSizeScope && this.dataset.variantSize) {
          this.skipSizeScope = true
          this.initTagalysWidget()
          return response
        }

        this.setComponentVisibility(false)
        return response
      }

      onFailedAPICall (error) {
        return error
      }

      renderCustomProducts (products) {
        const recommendationsContainer = this.getCarouselElement()
        if (!recommendationsContainer) {
          return
        }

        recommendationsContainer.innerHTML = ''

        const productsHTML = this.renderAllProducts(products)

        recommendationsContainer.innerHTML = productsHTML
      }

      renderAllProducts (products) {
        const productArray = []

        products.forEach(product => {
          productArray.push(this.renderProduct(product))
        })

        return productArray.join('')
      }

      renderProduct (product) {
        if (!this.hasRequiredProductData(product)) {
          return ''
        }

        const selectedVariant = this.getSelectedVariant(product)

        if (!selectedVariant) {
          return ''
        }

        return `
          <div
            class="car-Cards_Item keen-slider__slide"
            data-tagalys-push-cart-upsell-carousel-el="slide"
          >
            <article class="prd-Card util-FauxLink">
              ${this.renderProductImages(product)}

              <div class="prd-Card_Body fz-15_21">
                ${this.renderProductDetails(product)}
                ${this.renderProductColors(product)}
                ${this.renderProductPrice(product)}
                ${this.renderProductForm(product, selectedVariant)}
              </div>

              ${this.renderProductLink(product)}
            </article>
          </div>
        `
      }

      renderProductForm (product, selectedVariant) {
        const hasSelectedVariant =
          !!selectedVariant && selectedVariant.available
        const hasOneOption = this.productHasOneOption(product)
        const buttonDisabled =
          !product.has_only_default_variant &&
          !hasOneOption &&
          !hasSelectedVariant

        return `
          <product-form class="prd-Card_ProductForm">
            <form
              class="prd-Card_Form"
              method="post"
              action="/cart/add"
              accept-charset="UTF-8"
              enctype="multipart/form-data"
              data-product-handle="${product.handle}"
              data-push-cart-form=""
              data-product-form=""
              data-tagalys-product-form=""
              data-variants="${encodeURIComponent(
                JSON.stringify(product.variants),
              )}"
              onchange="window.BAO.Tagalys.handleProductCardFormChange(this)"
            >
              ${this.renderAddToCartButton(
                product,
                selectedVariant,
                buttonDisabled,
              )}
              ${this.renderFormHiddenInputs(product, selectedVariant)}
            </form>
          </product-form>
        `
      }

      productHasOneOption (product) {
        return product?.options_with_values?.[0]?.values?.length === 1
      }

      renderOptionSelectors (product, selectedVariant, hasOneOption) {
        if (!product?.options_with_values?.length) return ''

        return product.options_with_values
          .map(option => {
            return `
              <div class="prd-Card_Option">
                <select
                  class="frm-Form_Select fz-13_19 prd-Card_ProductFormSelect"
                  name="options[${option.name}]"
                  data-name="${option.name}"
                  data-option-select=""
                  data-product-form-el="select"
                >
                  <option value="" disabled selected>
                    ${window.theme.strings.select}
                  </option>

                  ${option.values
                    .map(value => {
                      // Find the variant that matches this option value
                      const associatedVariant = product?.variants?.find(
                        variant => variant?.title === value,
                      )
                      const isSoldOut = associatedVariant?.available === false

                      return `
                        <option value="${value}" ${isSoldOut ? 'disabled' : ''}>
                          ${value} ${
                        isSoldOut ? `(${window.theme.strings.soldOut})` : ''
                      }
                        </option>
                      `
                    })
                    .join('')}
                </select>
              </div>
            `
          })
          .join('')
      }

      getProductsInSelectedSize (products) {
        return (products || []).filter(product =>
          this.getSelectedVariant(product),
        )
      }

      getSelectedVariant (product) {
        return window.BAO.TagalysLibrary.getVariantForSize(
          product,
          this.dataset.variantSize,
          window.BAO.TagalysLibrary.getSizeOptionPosition(
            this.dataset.productId,
          ),
        )
      }

      getSelectedVariantId (product) {
        return this.getSelectedVariant(product)?.id || null
      }

      // Product rendering helper methods
      renderSecondImage (product) {
        if (!this.hasSecondImage(product)) return ''
        return `
          <div class="prd-Card_Image prd-Card_Image-hover">
            <img
              class="product-image"
              src="${product.images[1].src}"
              alt="${this.getProductTitle(product)}"
            />
          </div>
        `
      }

      renderSaleSticker (product) {
        if (!this.isOnSale(product)) return ''
        return `<p class="prd-Card_Sticker prd-Card_Sticker-sale fz-13_17">${window.theme.strings.stickerSale}</p>`
      }

      renderProductImages (product) {
        return `
          <div class="prd-Card_ImageContainer">
            <div class="prd-Card_Image">
              <img
                class="product-image"
                src="${window.BAO.TagalysLibrary.getProductImageUrl(product)}"
                alt="${this.getProductTitle(product)}"
              />
            </div>
            ${this.renderSecondImage(product)}
            ${this.renderSaleSticker(product)}
          </div>
        `
      }

      renderProductDetails (product) {
        const productTitle = this.getProductTitle(product)

        return `
          <div class="prd-Card_Details">
            <h3 class="prd-Card_Title" id="ProductCard-${product.id}">
              ${productTitle}
            </h3>
          </div>
        `
      }

      renderProductPrice (product) {
        const productPrice = this.formatProductPrice(product.price)
        const isOnSale = product.compare_at_price_max > product.price
        const compareAtPrice = this.formatProductPrice(product.compare_at_price)

        return `
          <div class="prd-Card_Price sis-Price">
            ${
              isOnSale
                ? `
              <s class="prd-Card_RegularPrice">
                <span class="util-ScreenReaderOnly">
                  ${window.theme.strings.price.regularPrice}
                </span>
                ${
                  product.compare_at_price > product.price ? compareAtPrice : ''
                }
              </s>

              <span class="prd-Card_SalePrice">
                ${productPrice}
              </span>
            `
                : productPrice
            }
          </div>
        `
      }

      renderProductColors (product) {
        const colourTitle = window.BAO.TagalysLibrary.getColourTitle(product)
        if (!colourTitle) return ''
        return `
          <div class="prd-Card_Colours">
            <p class="prd-Card_Colour prd-Card_Colour-current">
              ${colourTitle}
            </p>
          </div>
        `
      }

      renderProductLink (product) {
        return `
          <a href="${window.BAO.TagalysLibrary.getProductLink(
            product,
          )}" class="util-FauxLink_Link">
            <span class="util-ScreenReaderOnly">
              ${this.dataset.linkToTranslation ||
                window.theme?.strings?.linkTo ||
                'View'} ${this.getProductTitle(product)}
            </span>
          </a>
        `
      }

      // Product form helper methods
      renderFormOptions (product, selectedVariant, hasOneOption) {
        if (product.has_only_default_variant || hasOneOption) {
          return ''
        }

        return `
          <div class="prd-Card_Options">
            ${this.renderOptionSelectors(
              product,
              selectedVariant,
              hasOneOption,
            )}
          </div>
        `
      }

      renderAddToCartButton (
        product,
        selectedVariant,
        hasOneOption,
        buttonDisabled,
      ) {
        const buttonContent = product.has_only_default_variant
          ? this.renderDefaultVariantButtonContent(product)
          : this.renderMultiVariantButtonContent(product, hasOneOption)

        return `
          <button
            type="submit"
            name="add"
            class="prd-Card_Button bis-BackInStock_AtcButton btn-Button btn-Button-secondary btn-Button-fullWidth"
            data-product-add-to-cart=""
            data-product-form-el="addToCart"
            aria-hidden="false"
            ${buttonDisabled ? 'disabled' : ''}
          >
            ${buttonContent}
          </button>
        `
      }

      renderDefaultVariantButtonContent (product) {
        return `
          <span>
            <span class="prd-Card_ButtonText" data-product-add-to-cart-text="add" aria-hidden="${!product
              .variants?.[0]?.available}">
              ${window.theme.strings.addToCart}
            </span>

            <span class="prd-Card_ButtonText" data-product-add-to-cart-text="soldout" aria-hidden="${
              product.variants?.[0]?.available
            }">
              ${window.theme.strings.soldOut}
            </span>
          </span>
        `
      }

      renderMultiVariantButtonContent (product, hasOneOption) {
        return `
          <span>
            <span class="prd-Card_ButtonText" data-product-add-to-cart-text="add" aria-hidden="false">
              ${window.theme.strings.addToCart}
            </span>

            <span class="prd-Card_ButtonText" data-product-add-to-cart-text="soldout" aria-hidden="true">
              ${window.theme.strings.soldOut}
            </span>
          </span>
        `
      }

      renderFormHiddenInputs (product, selectedVariant) {
        return `
          <input type="hidden" name="id" data-variant-id value="${selectedVariant?.id ||
            ''}">
          <input type="hidden" name="form_type" value="product">
          <input type="hidden" name="utf8" value="✓">
          <input type="hidden" name="attributes[last_added_product_id]" value="${
            product.id
          }">
        `
      }

      // Utility methods for data transformations
      hasSecondImage (product) {
        return 1 in product.images
      }

      isOnSale (product) {
        return (
          product.compare_at_price !== product.price &&
          product.compare_at_price !== null
        )
      }

      getProductTitle (product) {
        return window.BAO.TagalysLibrary.getProductTitle(product)
      }

      formatProductPrice (price) {
        if (price === 0) return ''
        return window.BAO.Utils.formatMoney(price * 100, theme.moneyFormat)
      }

      // Visibility methods
      setComponentVisibility (isVisible) {
        const ariaHiddenValue = !isVisible
        this.setAttribute('aria-hidden', ariaHiddenValue)

        if (this.externalEls?.trigger?.exists) {
          this.externalEls.trigger.element.setAttribute(
            'aria-hidden',
            ariaHiddenValue,
          )
        }

        this.els?.recs?.element?.setAttribute('aria-hidden', ariaHiddenValue)
        this.externalEls?.recs?.element?.setAttribute(
          'aria-hidden',
          ariaHiddenValue,
        )
      }

      // Validation methods
      hasRequiredProductData (product) {
        return product && product.variants && product.handle
      }

      isValidTagalysResponse (response) {
        return response && Array.isArray(response.products)
      }

      hasEl (elementName) {
        return this.els[elementName] && this.els[elementName].exists
      }

      getEl (elementName) {
        return this.hasEl(elementName) ? this.els[elementName].element : null
      }
    },
  )
}
